import React from 'react';

import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import {LabelingStrategy, TagSchema} from 'constants/RegistryConstants';
import {ComplexPolymerInterfaceApiId} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import SchemaFieldSelectorManager from 'models/managers/SchemaFieldSelectorManager';
import {RegistrationTableType} from 'notebook/registration-tables/RegistrationTableConstants';
import {RegistrationTableTagSchemaSelector} from 'notebook/structured-tables/components/modals/RegistrationTableTagSchemaSelector';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import SchemaLabelingStrategyInput from 'registry/labeling-strategies/SchemaLabelingStrategyInput';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

interface ConnectedProps {
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
}

class RegistrationTableInsertConnector extends DataConnector<
  RegistrationTableInsertModalProps,
  ConnectedProps
> {
  stores = [SchemaFieldSelectorStore];

  enhanceProps(): ConnectedProps {
    const selectorsById = this.schemaFieldSelectorStore().getAllSelectorsById({includeArchived: true});
    return {
      selectorsById,
    };
  }
}

export interface RegistrationTableInsertModalProps {
  onRequestSubmit: (args: {
    tagSchema: TagSchema;
    tableType: string;
    labelingStrategy: string | null;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
  }) => void;
  onRequestClose: () => void;
}

interface State {
  selectedTagSchema: TagSchema | null;
  labelingStrategy: LabelingStrategy | null;
}

interface InnerRegistrationTableInsertProps {
  handleSelectedTagSchema: (tagSchema: {id: string}) => void;
  handleSelectedLabelingStrategy: (labelingStrategy: string) => void;
  shouldShowLabelingStrategyInput: () => boolean;
}

class InnerRegistrationTableInsertModal extends React.Component<InnerRegistrationTableInsertProps & State> {
  _handleSelectTagSchema = (tagSchema: {id: string} | null): void => {
    assert(tagSchema != null, 'TagSchemaSelector returned a null tag schema');
    this.props.handleSelectedTagSchema(tagSchema);
  };

  render(): JSX.Element {
    return (
      <>
        <RegistrationTableTagSchemaSelector
          selectedTagSchemaId={this.props.selectedTagSchema?.id ?? null}
          onChange={this._handleSelectTagSchema}
          // TODO(BNCH-128809): Make the legacy insert modal work for antibodies and complex polymers.
          ssisToExclude={[
            ANTIBODY_SCHEMA_INTERFACE_ID,
            ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID.toString(),
            ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID.toString(),
          ]}
        />
        {this.props.shouldShowLabelingStrategyInput() && (
          <div className="u-padding-top">
            <SchemaLabelingStrategyInput
              labelingStrategy={this.props.labelingStrategy}
              onChange={this.props.handleSelectedLabelingStrategy}
              tagSchemaId={this.props.selectedTagSchema?.id}
            />
          </div>
        )}
      </>
    );
  }
}

class LegacyRegistrationTableInsertModal extends React.Component<
  RegistrationTableInsertModalProps & ConnectedProps,
  State
> {
  state: State = {
    selectedTagSchema: null,
    labelingStrategy: null,
  };

  componentDidMount(): void {
    SchemaFieldSelectorManager.fetchForAllRegistries();
  }

  _allSelectorsLoaded(): boolean {
    const tagSchema = this.state.selectedTagSchema!;
    const selectorIds = getSelectorIdsFromFields(tagSchema.fields);
    return !selectorIds.some((selectorId) => this.props.selectorsById[selectorId] == null);
  }

  _handleInsert = (): void => {
    this.props.onRequestSubmit({
      tagSchema: this.state.selectedTagSchema!,
      tableType: RegistrationTableType.FILE,
      labelingStrategy: this.state.labelingStrategy,
      selectorsById: this.props.selectorsById,
    });
  };

  _handleLabelingStrategyChange = (labelingStrategy: LabelingStrategy): void => {
    this.setState({labelingStrategy});
  };

  _handleSelectTagSchema = (selectedTagSchema: TagSchema): void => {
    this.setState({selectedTagSchema});
  };

  _shouldShowLabelingStrategyInput = (): boolean => Boolean(this.state.selectedTagSchema);

  render(): JSX.Element {
    return (
      <Modal
        title="Insert registration table"
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this._handleInsert}
        buttons={[
          {
            text: 'Insert',
            submit: true,
            style: ButtonStyle.SECONDARY,
            disabled: !this.state.selectedTagSchema || !this._allSelectorsLoaded(),
            dataTestAttrs: {component: 'LegacyRegistrationTableInsertModal', key: ModalButtonKey.INSERT},
          },
        ]}
      >
        <InnerRegistrationTableInsertModal
          {...this.state}
          handleSelectedTagSchema={this._handleSelectTagSchema}
          handleSelectedLabelingStrategy={this._handleLabelingStrategyChange}
          shouldShowLabelingStrategyInput={this._shouldShowLabelingStrategyInput}
        />
      </Modal>
    );
  }
}

export default RegistrationTableInsertConnector.connect(LegacyRegistrationTableInsertModal);
